

/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React contexts
import { useMaterialUIController } from "context";

function DataTableHeadCell({
  width,
  children,
  sorted,
  align,
  colKey,
  fixedLayout,
  onResizeStart,
  onResizeReset,
  ...rest
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <MDBox
      component="th"
      width={width}
      data-col-key={colKey}
      data-table-head-cell
      py={1.25}
      px={2}
      sx={{
        borderBottom: "none",
        position: "relative",
        // PSA-373: resized headers wrap instead of clipping with "...".
        ...(fixedLayout && {
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          verticalAlign: "middle",
        }),
        "&:hover [data-col-resizer]::after": { opacity: 1 },
      }}
    >
      <MDBox
        {...rest}
        display="inline-flex"
        alignItems="center"
        justifyContent={align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start"}
        gap={0.5}
        color={darkMode ? "white" : "secondary"}
        opacity={0.85}
        sx={({ typography: { size, fontWeightMedium, families } }) => ({
          fontFamily: families?.body || '"Hanken Grotesk", sans-serif',
          fontSize: size.xxs,
          fontWeight: fontWeightMedium,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          cursor: sorted && "pointer",
          userSelect: sorted && "none",
          width: "100%",
          ...(fixedLayout && {
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            lineHeight: 1.3,
          }),
        })}
      >
        {children}
        {sorted && (
          <MDBox
            display="inline-flex"
            flexDirection="column"
            alignItems="center"
            lineHeight={0}
            sx={({ typography: { size } }) => ({
              fontSize: size.lg,
              flexShrink: 0,
            })}
          >
            <Icon
              sx={{
                fontSize: "1.1rem !important",
                lineHeight: 1,
                color: sorted === "asce" ? "text.main" : "secondary.main",
                opacity: sorted === "asce" ? 1 : 0.5,
                marginBottom: "-6px",
              }}
            >
              arrow_drop_up
            </Icon>
            <Icon
              sx={{
                fontSize: "1.1rem !important",
                lineHeight: 1,
                color: sorted === "desc" ? "text.main" : "secondary.main",
                opacity: sorted === "desc" ? 1 : 0.5,
                marginTop: "-6px",
              }}
            >
              arrow_drop_down
            </Icon>
          </MDBox>
        )}
      </MDBox>
      {onResizeStart && (
        <MDBox
          data-col-resizer
          role="separator"
          aria-orientation="vertical"
          title="Drag to resize, double-click to reset"
          onPointerDown={onResizeStart}
          onDoubleClick={onResizeReset}
          onClick={(e) => e.stopPropagation()}
          sx={({ palette }) => ({
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: 10,
            cursor: "col-resize",
            touchAction: "none",
            userSelect: "none",
            zIndex: 1,
            "&::after": {
              content: '""',
              position: "absolute",
              top: "25%",
              bottom: "25%",
              right: 4,
              width: 2,
              borderRadius: 1,
              backgroundColor: palette.grey?.[400] || "#ced4da",
              opacity: 0,
              transition: "opacity 0.15s ease",
            },
            "&:hover::after, &:active::after": {
              opacity: 1,
              backgroundColor: palette.info?.main || "#1A73E8",
            },
          })}
        />
      )}
    </MDBox>
  );
}

// Setting default values for the props of DataTableHeadCell
DataTableHeadCell.defaultProps = {
  width: "auto",
  sorted: "none",
  align: "left",
  colKey: undefined,
  fixedLayout: false,
  onResizeStart: undefined,
  onResizeReset: undefined,
};

// Typechecking props for the DataTableHeadCell
DataTableHeadCell.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node.isRequired,
  sorted: PropTypes.oneOf([false, "none", "asce", "desc"]),
  align: PropTypes.oneOf(["left", "right", "center"]),
  colKey: PropTypes.string,
  fixedLayout: PropTypes.bool,
  onResizeStart: PropTypes.func,
  onResizeReset: PropTypes.func,
};

export default DataTableHeadCell;
